import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import LazyOnScroll from "../../ui/LazyOnScroll";

interface CarouselItem {
  title: string;
  description: string;
  image: { asset: { url: string } };
}

interface CarouselProps {
  items: CarouselItem[];
  autoPlay?: boolean;
  interval?: number;
}

export function Carousel({
  items,
  autoPlay = true,
  interval = 5000,
}: CarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [firstImageLoaded, setFirstImageLoaded] = useState(false);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prevIndex) =>
      prevIndex === items.length - 1 ? 0 : prevIndex + 1
    );
  }, [items.length]);

  useEffect(() => {
    if (!autoPlay || !firstImageLoaded) return;

    const intervalId = setInterval(() => {
      nextSlide();
    }, interval);

    return () => clearInterval(intervalId);
  }, [autoPlay, interval, nextSlide, firstImageLoaded]);

  return (
    <LazyOnScroll>
      <div className="relative w-full overflow-hidden">
        <div className="relative h-[50vh] md:h-[70vh] w-full">
          {items.map((item, index) => {
            const imageUrl = `${item.image.asset.url}?w=1600&auto=format`;

            const isActive = index === currentIndex;
            const isFirst = index === 0;

            return (
              <div
                key={item.image.asset.url}
                className={`absolute inset-0 transition-opacity duration-1000 ${
                  isActive
                    ? "opacity-100 z-10"
                    : "opacity-0 pointer-events-none z-0"
                }`}
              >
                <div className="relative w-full h-full">
                  <img
                    src={imageUrl}
                    alt={item.title}
                    decoding="async"
                    className={`w-full h-full object-cover transition-opacity duration-700 ${
                      !firstImageLoaded && isFirst
                        ? "opacity-0"
                        : isActive
                        ? "opacity-100"
                        : "opacity-0"
                    }`}
                    loading={isFirst ? "eager" : "lazy"}
                    onLoad={() => {
                      if (isFirst) setFirstImageLoaded(true);
                    }}
                  />

                  {/* Show overlay + text only when first image is loaded or this is not the first slide */}
                  {(firstImageLoaded || !isFirst) && isActive && (
                    <>
                      <div className="absolute inset-0 bg-black/60" />
                      <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-4 z-20">
                        <div className="max-w-3xl">
                          <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
                            {item.title}
                          </h2>
                          <p className="text-lg md:text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                            {item.description}
                          </p>
                          <Link
                            to="/portfolio"
                            className="inline-flex h-10 items-center justify-center rounded-md px-8 text-sm font-medium shadow transition-colors bg-[#484331] hover:bg-[#3e3a2b] text-white focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                          >
                            View Project
                          </Link>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </LazyOnScroll>
  );
}
